import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Helper from '../../helpers/Helper';
import WaitsDataTable from '../../types/instance/tables/WaitsDataTable';
import UsersDataTable from '../../types/instance/tables/UsersDataTable';
import ProgramsDataTable from '../../types/instance/tables/ProgramsDataTable';
import SessionsDataTable from '../../types/instance/tables/SessionsDataTable';
import DatabasesDataTable from '../../types/instance/tables/DatabasesDataTable';
import ClientsDataTable from '../../types/instance/tables/ClientsDataTable';
import Change from '../../types/instance/Change';
import Alert from '../../types/instance/Alert';
import Event from '../../types/instance/Event';

dayjs.extend(utc);
dayjs.extend(timezone);

const MAX_TIME_SERIES_ROWS = 200;
const TOP_N = 10;
const SEVERITY_LABEL: Record<number, string> = { 0: 'normal', 1: 'critical', 2: 'warning', 3: 'info' };
const MAX_SQL_TEXT_LENGTH = 120;

function formatTimestamp(tsMs: number, tz: string): string {
    return dayjs(tsMs).tz(tz).format('YYYY-MM-DD HH:mm:ss');
}

function formatTopTable(rows: { name: string; waittime: number }[], title: string): string {
    if (rows.length === 0) return '';
    const lines = rows.slice(0, TOP_N).map((r, i) => `  ${i + 1}. ${r.name}: ${Helper.getTimeInEnglish(r.waittime)}`);
    return `\n${title}:\n${lines.join('\n')}`;
}

const Prompts = {

    getStatementTextPrompt(statement: string, executionPlan: string, instanceType: string): string {
        return `I have a sql statement for ${instanceType}: ${statement}, With execution plan: ${executionPlan}. How can I tune it?`
    },

    getInstanceWaitsPrompt(waits: string[], instanceType: string | undefined ): string {
        const isMoreThanOne = waits.length > 1
        return `My ${instanceType} database is spending a lot of time in "${waits.join(', ')}". How can I reduce ${isMoreThanOne ? 'these' : 'this'}?`;
    },

    getInstanceWaitPrompt(wait: string, instanceType: string | undefined ): string {
        return `What does the wait event "${wait}" mean in ${instanceType} and how can I reduce it?`;
    },

    getDeadLockPrompt(deadLock: string, instanceType: string | undefined ): string {
        return `My ${instanceType} database had a deadlock: ${deadLock} How can I avoid this happening?`;
    },

    getConnectivityErrorPrompt(message: string, dbType: string, targetKind: 'database' | 'host'): string {
        return `I am getting a connectivity error when trying to connect to my ${dbType} ${targetKind}: "${message}". What are the likely causes and how can I resolve this?`;
    },

    getActivityOverviewPrompt(
        instanceType: string | undefined,
        from: string,
        to: string,
        timezone: string,
        totalTime: number,
        executions: number,
        timeSeries: number[][],
        executionSeries: number[][],
        statements: { sqltext: string, sqlhash?: string, grouphash?: string, waittime: number }[],
        waits: WaitsDataTable[],
        users: UsersDataTable[],
        programs: ProgramsDataTable[],
        sessions: SessionsDataTable[],
        databases: DatabasesDataTable[],
        clients: ClientsDataTable[],
        changes: Change[],
        alerts: Alert[],
        events: Event[]
    ): string {
        const avgTime = executions > 0 ? totalTime / executions : 0;

        // Downsample time series if it exceeds the row limit
        let sampledSeries = timeSeries;
        if (timeSeries.length > MAX_TIME_SERIES_ROWS) {
            const step = Math.ceil(timeSeries.length / MAX_TIME_SERIES_ROWS);
            sampledSeries = timeSeries.filter((_, i) => i % step === 0);
        }

        // Build execution lookup by timestamp
        const execByTs = new Map<number, number>();
        executionSeries.forEach(([ts, count]) => execByTs.set(ts, count));

        // Collect all events with timestamps for inline annotation
        const allAnnotations: { ts: number; label: string }[] = [
            ...changes.map(c => ({
                ts: new Date(c.timeslice).getTime(),
                label: `[Change] ${c.name}${c.oldvalue != null && c.newvalue != null ? `: ${c.oldvalue} → ${c.newvalue}` : ''}`
            })),
            ...alerts.map(a => ({
                ts: new Date(a.timeslice).getTime(),
                label: `[Alert] ${a.alertType} (${a.severity != null ? SEVERITY_LABEL[a.severity] ?? a.severity : 'unknown'})`
            })),
            ...events.map(e => ({
                ts: new Date(e.timeslice).getTime(),
                label: `[Event] ${e.title} (${e.eventTypeName})`
            }))
        ];

        // Map each annotation to its single closest time-series row to avoid duplicates
        const annotationsMap = new Map<number, string[]>();
        if (sampledSeries.length > 0) {
            for (const ann of allAnnotations) {
                let closestTs = sampledSeries[0][0];
                let closestDist = Math.abs(ann.ts - closestTs);
                for (const [ts] of sampledSeries) {
                    const dist = Math.abs(ann.ts - ts);
                    if (dist < closestDist) {
                        closestDist = dist;
                        closestTs = ts;
                    }
                }
                if (!annotationsMap.has(closestTs)) annotationsMap.set(closestTs, []);
                annotationsMap.get(closestTs)!.push(ann.label);
            }
        }

        // Build time series lines with event annotations
        const timeSeriesLines = sampledSeries.map(([ts, waittime]) => {
            const execs = execByTs.get(ts);
            const execStr = execs !== undefined ? ` | ${execs.toLocaleString()} execs` : '';
            const annotations = annotationsMap.get(ts) ?? [];
            const matchStr = annotations.map(l => `← ${l}`).join('  ');
            return `${formatTimestamp(ts, timezone)} | ${Helper.getTimeInEnglish(waittime)}${execStr}${matchStr ? `  ${matchStr}` : ''}`;
        });

        // Build top contributor sections (order matches the UI)
        const sqlRows = statements.map(s => ({
            name: s.sqltext ? (s.sqltext.length > MAX_SQL_TEXT_LENGTH ? s.sqltext.substring(0, MAX_SQL_TEXT_LENGTH) + '...' : s.sqltext) : (s.sqlhash ?? s.grouphash ?? ''),
            waittime: s.waittime
        }));
        const topSections = [
            formatTopTable(sqlRows, 'Top SQL Statements (total time for period)'),
            formatTopTable(waits.map(w => ({ name: w.waitevent, waittime: w.waittime })), 'Top Wait Events (total time for period)'),
            formatTopTable(databases.map(d => ({ name: d.database, waittime: d.waittime })), 'Top Databases (total time for period)'),
            formatTopTable(sessions.map(s => ({ name: s.session, waittime: s.waittime })), 'Top Sessions (total time for period)'),
            formatTopTable(clients.map(c => ({ name: c.client, waittime: c.waittime })), 'Top Clients (total time for period)'),
            formatTopTable(users.map(u => ({ name: u.username, waittime: u.waittime })), 'Top Users (total time for period)'),
            formatTopTable(programs.map(p => ({ name: p.program, waittime: p.waittime })), 'Top Programs (total time for period)'),
        ].join('');

        const timeSeriesSection = timeSeriesLines.length > 0
            ? `\nTime series (${timezone} timestamps, wait time | executions per interval):\n${timeSeriesLines.join('\n')}`
            : '';

        return `Here is activity data for my ${instanceType ?? 'database'} database from ${from} to ${to} (${timezone}):

Summary:
- Total Time: ${Helper.getTimeInEnglish(totalTime)}
- Total Executions: ${executions.toLocaleString()}
- Average Time per Execution: ${Helper.getTimeInEnglish(avgTime)}
${timeSeriesSection}${topSections}

Please analyse this data and answer:
1. Are there any notable trends or patterns in the wait time or execution volume over the period?
2. Do any of the events (changes, alerts, custom events) coincide with changes in performance?
3. Which statements, users, programs, or wait events appear to be the main contributors to any performance issues?
4. Are there any periods of unusually high or low activity worth investigating?`;
    },

};

export default Prompts;
